import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ArrowLeft, TrendingUp, Package, Minus, Plus, Truck, ShoppingCart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatusTag({ status }) {
  const styles = {
    live: 'bg-[#6EE7B7] border-[2px] border-[#121212] shadow-[2px_2px_0px_0px_#121212]',
    hot: 'bg-[#FF6B6B] border-[2px] border-[#121212] shadow-[2px_2px_0px_0px_#121212]',
    sold_out: 'bg-[#D8DFEA] border-[2px] border-[#121212] shadow-[2px_2px_0px_0px_#121212]',
  };
  return (
    <span className={`${styles[status] || styles.live} text-[#121212] text-xs font-black px-3 py-1 rounded-full uppercase tracking-wider`}>
      {status === 'sold_out' ? 'SOLD OUT' : status.toUpperCase()}
    </span>
  );
}

export default function ListingDetail() {
  const { listingId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [showClaimForm, setShowClaimForm] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState('');
  const [fulfillmentType, setFulfillmentType] = useState('');
  const [shipping, setShipping] = useState({ name: '', phone: '', address: '', city: 'Bangalore', pincode: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    axios.get(`${API}/listings/${listingId}`)
      .then(r => {
        setListing(r.data);
        if (r.data.size_options?.length > 0) setSelectedSize(r.data.size_options[0]);
      })
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
  }, [listingId, navigate]);

  const validateShipping = () => {
    const errors = {};
    if (!shipping.name.trim() || shipping.name.trim().length < 2) errors.name = 'Full name required (min 2 chars)';
    if (!shipping.phone.trim()) errors.phone = 'Phone number required';
    else if (!/^[6-9]\d{9}$/.test(shipping.phone.replace(/\s/g, ''))) errors.phone = 'Enter valid 10-digit Indian mobile number';
    if (!shipping.address.trim() || shipping.address.trim().length < 10) errors.address = 'Full address required (min 10 chars)';
    const cityNorm = shipping.city.trim().toLowerCase();
    if (!cityNorm) errors.city = 'City required';
    else if (!['bangalore', 'bengaluru', 'blr'].includes(cityNorm)) errors.city = 'We currently ship only to Bangalore';
    if (!shipping.pincode.trim()) errors.pincode = 'Pincode required';
    else if (!/^560\d{3}$/.test(shipping.pincode.trim())) errors.pincode = 'Enter valid Bangalore pincode (560xxx)';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleClaim = async () => {
    if (!user) {
      window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google?t=${Date.now()}`;
      return;
    }
    if (fulfillmentType === 'ship_to_me' && !validateShipping()) return;
    setClaiming(true);
    setError('');
    try {
      const payload = {
        listing_id: listingId,
        quantity_claimed: quantity,
        selected_size: selectedSize,
        fulfillment_type: fulfillmentType,
      };
      if (fulfillmentType === 'ship_to_me') {
        payload.shipping_name = shipping.name;
        payload.shipping_phone = shipping.phone;
        payload.shipping_address = shipping.address;
        payload.shipping_city = shipping.city;
        payload.shipping_pincode = shipping.pincode;
      }
      await axios.post(`${API}/claims`, payload, { withCredentials: true });
      setSuccess(
        fulfillmentType === 'buy_myself'
          ? `Claimed ${quantity} shirt${quantity > 1 ? 's' : ''}! You'll buy it from the startup's store.`
          : `Claimed ${quantity} shirt${quantity > 1 ? 's' : ''}! It'll be shipped to you.`
      );
      setShowClaimForm(false);
      setFulfillmentType('');
      const r = await axios.get(`${API}/listings/${listingId}`);
      setListing(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Claim failed');
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F6F1E8' }}>
        <div className="animate-spin w-8 h-8 border-4 border-[#121212] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!listing) return null;

  const pct = Math.min((listing.claimed_count / listing.total_inventory) * 100, 100);
  const isSoldOut = listing.status === 'sold_out';
  const inputClass = "w-full bg-white border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] rounded-xl p-3 font-medium text-sm focus:outline-none focus:-translate-y-[1px] focus:shadow-[4px_4px_0px_0px_#121212] transition-all";

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F6F1E8' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider mb-6 hover:text-[#FF6B6B] transition-colors" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" strokeWidth={3} /> Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left - Image / Info */}
          <div className="lg:col-span-3">
            {/* T-shirt mockup area */}
            <div className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[6px_6px_0px_0px_#121212] rounded-2xl overflow-hidden mb-6">
              <div className="h-4 border-b-[2px] border-[#121212]" style={{ backgroundColor: getColorHex(listing.tshirt_color) }} />
              <div className="flex items-center justify-center py-20 px-8">
                <div className="text-center">
                  <Package className="w-16 h-16 mx-auto mb-4" strokeWidth={1.5} style={{ color: '#aaa' }} />
                  <p className="text-lg font-bold">{listing.title}</p>
                  <p className="text-sm font-medium mt-1" style={{ color: '#888' }}>{listing.tshirt_color} tee</p>
                </div>
              </div>
            </div>

            {/* Startup info */}
            <div className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl border-[3px] border-[#121212] flex items-center justify-center font-black text-lg" style={{ backgroundColor: '#F6F1E8' }}>
                  {(listing.startup_name || 'S')[0]}
                </div>
                <div>
                  <h3 className="font-bold text-lg">{listing.startup_name}</h3>
                  {listing.tagline && <p className="text-sm font-medium" style={{ color: '#666' }}>{listing.tagline}</p>}
                </div>
              </div>
              {listing.description && <p className="text-sm font-medium mb-3" style={{ color: '#555' }}>{listing.description}</p>}
              {listing.website && (
                <a href={listing.website} target="_blank" rel="noopener noreferrer" className="text-sm font-bold underline underline-offset-4 hover:text-[#FF6B6B]">
                  {listing.website}
                </a>
              )}
            </div>

            {/* Bid History */}
            {listing.bid_history?.length > 0 && (
              <div className="mt-6 bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] rounded-2xl p-6">
                <h3 className="font-bold text-base uppercase tracking-wider mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" strokeWidth={2.5} /> Bid History
                </h3>
                <div className="space-y-2">
                  {listing.bid_history.map(bh => (
                    <div key={bh.history_id} className="flex items-center justify-between text-sm font-mono" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      <span>${bh.old_bid} → ${bh.new_bid}</span>
                      <span className="text-xs" style={{ color: '#888' }}>{getTimeAgo(bh.changed_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right - Bid & Claim */}
          <div className="lg:col-span-2">
            <div className="sticky top-24">
              <div className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[6px_6px_0px_0px_#121212] rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <StatusTag status={listing.status} />
                  {listing.trend && (
                    <span className="font-mono text-xs font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: listing.trend.includes('↑') ? '#16a34a' : '#888' }}>
                      {listing.trend}
                    </span>
                  )}
                </div>

                <div className="mb-6">
                  <div className="text-xs font-black uppercase tracking-wider mb-1" style={{ color: '#888' }}>Bid per shirt</div>
                  <div className="font-mono text-5xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="listing-bid">
                    ${listing.bid_per_shirt}
                  </div>
                </div>

                {/* Progress */}
                <div className="mb-6">
                  <div className="flex justify-between text-xs font-black uppercase tracking-wider mb-2" style={{ color: '#888' }}>
                    <span>Claimed</span>
                    <span>{listing.claimed_count} / {listing.total_inventory}</span>
                  </div>
                  <div className="hm-progress">
                    <div className="hm-progress-fill" style={{ width: `${pct}%`, backgroundColor: isSoldOut ? '#D8DFEA' : listing.status === 'hot' ? '#FF6B6B' : '#6EE7B7' }} />
                  </div>
                  <div className="font-mono text-sm font-bold mt-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {listing.remaining_count} remaining
                  </div>
                </div>

                {/* Sizes */}
                {listing.size_options?.length > 0 && (
                  <div className="mb-6">
                    <div className="text-xs font-black uppercase tracking-wider mb-2" style={{ color: '#888' }}>Size</div>
                    <div className="flex flex-wrap gap-2">
                      {listing.size_options.map(s => (
                        <button
                          key={s}
                          onClick={() => setSelectedSize(s)}
                          className={`text-xs font-black uppercase border-[2px] border-[#121212] rounded-lg px-3 py-1.5 transition-all ${
                            selectedSize === s ? 'bg-[#121212] text-[#F6F1E8]' : 'bg-[#F6F1E8] shadow-[2px_2px_0px_0px_#121212] hover:-translate-y-[1px]'
                          }`}
                          data-testid={`size-${s}`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quantity */}
                {!isSoldOut && (
                  <div className="mb-6">
                    <div className="text-xs font-black uppercase tracking-wider mb-2" style={{ color: '#888' }}>Quantity</div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="w-10 h-10 bg-[#F6F1E8] border-[3px] border-[#121212] rounded-xl flex items-center justify-center shadow-[2px_2px_0px_0px_#121212] active:translate-y-[2px] active:shadow-none"
                        data-testid="qty-minus"
                      >
                        <Minus className="w-4 h-4" strokeWidth={3} />
                      </button>
                      <span className="font-mono text-2xl font-bold w-12 text-center" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="qty-value">{quantity}</span>
                      <button
                        onClick={() => setQuantity(Math.min(listing.remaining_count, quantity + 1))}
                        className="w-10 h-10 bg-[#F6F1E8] border-[3px] border-[#121212] rounded-xl flex items-center justify-center shadow-[2px_2px_0px_0px_#121212] active:translate-y-[2px] active:shadow-none"
                        data-testid="qty-plus"
                      >
                        <Plus className="w-4 h-4" strokeWidth={3} />
                      </button>
                    </div>
                    <div className="font-mono text-sm font-bold mt-2" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#888' }}>
                      Total: ${(listing.bid_per_shirt * quantity).toLocaleString()}
                    </div>
                  </div>
                )}

                {success && (
                  <div className="mb-4 bg-[#6EE7B7] border-[2px] border-[#121212] rounded-xl p-3 text-sm font-bold text-center" data-testid="claim-success">
                    {success}
                  </div>
                )}
                {error && (
                  <div className="mb-4 bg-[#FF6B6B] border-[2px] border-[#121212] rounded-xl p-3 text-sm font-bold text-center" data-testid="claim-error">
                    {error}
                  </div>
                )}

                {/* Step 1: Choose Fulfillment (before showing form) */}
                {!isSoldOut && !showClaimForm && !success && !fulfillmentType && (
                  <div>
                    <div className="text-xs font-black uppercase tracking-wider mb-3" style={{ color: '#888' }}>
                      How do you want it?
                    </div>
                    <div className="space-y-3">
                      <button
                        data-testid="choose-buy-myself"
                        onClick={() => {
                          if (!user) {
                            window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google?t=${Date.now()}`;
                            return;
                          }
                          setFulfillmentType('buy_myself');
                          setShowClaimForm(true);
                        }}
                        className="w-full bg-[#E8C86A] text-[#121212] font-black uppercase border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] active:translate-y-[4px] active:shadow-none transition-all rounded-xl px-5 py-4 flex items-center gap-3"
                      >
                        <ShoppingCart className="w-5 h-5" strokeWidth={2.5} />
                        <div className="text-left flex-1">
                          <div className="text-sm">Buy it myself</div>
                          <div className="text-xs font-medium normal-case" style={{ color: '#555' }}>Buy from their store, get reimbursed</div>
                        </div>
                      </button>
                      <button
                        data-testid="choose-ship-to-me"
                        onClick={() => {
                          if (!user) {
                            window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google?t=${Date.now()}`;
                            return;
                          }
                          setFulfillmentType('ship_to_me');
                          setShowClaimForm(true);
                        }}
                        className="w-full bg-[#FF6B6B] text-[#121212] font-black uppercase border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] active:translate-y-[4px] active:shadow-none transition-all rounded-xl px-5 py-4 flex items-center gap-3"
                      >
                        <Truck className="w-5 h-5" strokeWidth={2.5} />
                        <div className="text-left flex-1">
                          <div className="text-sm">Ship it to me</div>
                          <div className="text-xs font-medium normal-case" style={{ color: '#3a1010' }}>We deliver to your door (Bangalore only)</div>
                        </div>
                      </button>
                    </div>
                  </div>
                )}

                {isSoldOut && (
                  <div className="w-full bg-[#D8DFEA] text-[#121212] font-black uppercase text-lg border-[3px] border-[#121212] rounded-xl px-6 py-4 text-center">
                    Sold Out
                  </div>
                )}

                {/* Claim Form: Buy Myself */}
                {showClaimForm && fulfillmentType === 'buy_myself' && (
                  <div className="space-y-3 animate-fade-in-up">
                    <div className="flex items-center gap-2 mb-1">
                      <ShoppingCart className="w-4 h-4" strokeWidth={2.5} />
                      <span className="text-xs font-black uppercase tracking-wider">Buy it Yourself</span>
                    </div>
                    <div className="bg-[#E8C86A]/30 border-[2px] border-[#E8C86A] rounded-lg px-3 py-2 text-xs font-bold" data-testid="buy-myself-info">
                      You'll buy the tee directly from the startup's store. The bid amount (${listing.bid_per_shirt}/shirt) is your reward for wearing it.
                    </div>
                    <button
                      data-testid="claim-submit-btn"
                      onClick={handleClaim}
                      disabled={claiming}
                      className="w-full bg-[#E8C86A] text-[#121212] font-black uppercase border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] active:translate-y-[4px] active:shadow-none transition-all rounded-xl px-6 py-3 disabled:opacity-50"
                    >
                      {claiming ? 'Claiming...' : `Confirm Claim — ${quantity} shirt${quantity > 1 ? 's' : ''}`}
                    </button>
                    <button
                      onClick={() => { setShowClaimForm(false); setFulfillmentType(''); }}
                      className="w-full text-sm font-bold text-center mt-2 hover:text-[#FF6B6B]"
                      data-testid="cancel-claim"
                    >
                      Cancel
                    </button>
                  </div>
                )}

                {/* Claim Form: Ship to Me (with shipping details) */}
                {showClaimForm && fulfillmentType === 'ship_to_me' && (
                  <div className="space-y-3 animate-fade-in-up">
                    <div className="flex items-center gap-2 mb-1">
                      <Truck className="w-4 h-4" strokeWidth={2.5} />
                      <span className="text-xs font-black uppercase tracking-wider">Shipping Details</span>
                    </div>
                    <div className="bg-[#E8C86A]/30 border-[2px] border-[#E8C86A] rounded-lg px-3 py-2 text-xs font-bold" data-testid="blr-only-notice">
                      Currently shipping only to Bangalore
                    </div>
                    <div>
                      <input data-testid="shipping-name" className={`${inputClass} ${fieldErrors.name ? 'border-[#FF6B6B]' : ''}`} placeholder="Full Name *" value={shipping.name} onChange={e => setShipping(p => ({...p, name: e.target.value}))} />
                      {fieldErrors.name && <p className="text-xs font-bold mt-1" style={{ color: '#FF6B6B' }}>{fieldErrors.name}</p>}
                    </div>
                    <div>
                      <input data-testid="shipping-phone" className={`${inputClass} ${fieldErrors.phone ? 'border-[#FF6B6B]' : ''}`} placeholder="Phone (10 digits) *" value={shipping.phone} onChange={e => setShipping(p => ({...p, phone: e.target.value.replace(/\D/g, '').slice(0, 10)}))} />
                      {fieldErrors.phone && <p className="text-xs font-bold mt-1" style={{ color: '#FF6B6B' }}>{fieldErrors.phone}</p>}
                    </div>
                    <div>
                      <input data-testid="shipping-address" className={`${inputClass} ${fieldErrors.address ? 'border-[#FF6B6B]' : ''}`} placeholder="Full Address *" value={shipping.address} onChange={e => setShipping(p => ({...p, address: e.target.value}))} />
                      {fieldErrors.address && <p className="text-xs font-bold mt-1" style={{ color: '#FF6B6B' }}>{fieldErrors.address}</p>}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <input data-testid="shipping-city" className={`${inputClass} ${fieldErrors.city ? 'border-[#FF6B6B]' : ''} bg-[#F6F1E8]`} placeholder="City *" value={shipping.city} readOnly title="Currently shipping only to Bangalore" />
                        {fieldErrors.city && <p className="text-xs font-bold mt-1" style={{ color: '#FF6B6B' }}>{fieldErrors.city}</p>}
                      </div>
                      <div>
                        <input data-testid="shipping-pincode" className={`${inputClass} ${fieldErrors.pincode ? 'border-[#FF6B6B]' : ''}`} placeholder="Pincode (560xxx) *" value={shipping.pincode} onChange={e => setShipping(p => ({...p, pincode: e.target.value.replace(/\D/g, '').slice(0, 6)}))} />
                        {fieldErrors.pincode && <p className="text-xs font-bold mt-1" style={{ color: '#FF6B6B' }}>{fieldErrors.pincode}</p>}
                      </div>
                    </div>
                    <button
                      data-testid="claim-submit-btn"
                      onClick={handleClaim}
                      disabled={claiming}
                      className="w-full bg-[#FF6B6B] text-[#121212] font-black uppercase border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] active:translate-y-[4px] active:shadow-none transition-all rounded-xl px-6 py-3 disabled:opacity-50"
                    >
                      {claiming ? 'Claiming...' : `Confirm Claim — $${(listing.bid_per_shirt * quantity).toLocaleString()}`}
                    </button>
                    <button
                      onClick={() => { setShowClaimForm(false); setFulfillmentType(''); }}
                      className="w-full text-sm font-bold text-center mt-2 hover:text-[#FF6B6B]"
                      data-testid="cancel-claim"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>

              {/* Info note */}
              <div className="mt-4 text-xs font-medium text-center" style={{ color: '#aaa' }}>
                {listing.total_claims || 0} total claims on this listing
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function getColorHex(color) {
  const map = { black: '#1a1a1a', white: '#f5f5f5', cream: '#f5f0e1', navy: '#1e3a5f', sage: '#9caf88', neon: '#39ff14' };
  return map[color] || '#EDE3C7';
}

function getTimeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
